import { backendTransportRequest } from "./backendTransport";
// jwt-decode exports a default function; import it as the default and avoid passing a type
// argument directly to the call because some builds treat the function as untyped.

import { jwtDecode } from "jwt-decode"; // named import

const authRequest = <T = any>(
  path: string,
  body?: Record<string, any>,
  accessToken?: string | null
) => backendTransportRequest<T>(path, { body, accessToken });

export const decodeJWT = (token: string): DecodedJWT | null => {
  try {
    return jwtDecode(token) as DecodedJWT;
  } catch (error) {
    exceptionService.logError(error, "Auth.decodeJWT");
    return null;
  }
};

// Provide small web-friendly shims for native/mobile libs and missing utilities
// Local storage based key storage (NOT secure) used for web builds. Replace with secure storage when available.
const Keychain = {
  async setGenericPassword(username: string, password: string, options?: { service?: string }) {
    const key = options?.service || username || "keychain";
    localStorage.setItem(key, JSON.stringify({ username, password }));
    return true;
  },
  async getGenericPassword(options?: { service?: string }) {
    const key = options?.service || "keychain";
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },
  async resetGenericPassword(options?: { service?: string }) {
    const key = options?.service || "keychain";
    localStorage.removeItem(key);
    return true;
  },
};

// Minimal helpers used by auth service
const stripPhoneNumber = (phone = "") => phone.replace(/\D/g, "");

const exceptionService = {
  logError: (err: any, context?: string) => {
    // eslint-disable-next-line no-console
    console.error("[exceptionService]", context, err);
  },
};

export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

export interface DecodedJWT {
  exp: number;
  [key: string]: any;
}

// Get valid access token (either from storage or refreshed)
export const getValidAccessToken = async (): Promise<string | null> => {
  try {
    const tokens = await getTokens();

    if (tokens && tokens.accessToken) {
      // Check if the token is expired
      // if (isTokenExpired(tokens.accessToken)) {
      //   //console.log('Token expired, refreshing...');
      //   const newAccessToken = await refreshAccessToken(tokens.refreshToken);
      //   if (newAccessToken) {
      //     // Store the new access token
      //     await storeTokens(newAccessToken, tokens.refreshToken);
      //     return newAccessToken;
      //   }
      // } else {
      return tokens.accessToken;
      //}
    }
    console.debug("No valid tokens found");
    return null;
  } catch (error) {
    exceptionService.logError(error, "Auth.getValidAccessToken");
    throw error;
  }
};

export const getCurrentSessionToken = async (): Promise<string | null> => {
  try {
    return await getValidAccessToken();
  } catch (error) {
    console.debug("error getting current session token", error);
    exceptionService.logError(error, "Auth.getCurrentSessionToken");
    throw error;
  }
};

function timeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error("Operation timed out"));
    }, ms);

    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

// Store tokens securely
export const storeTokens = async (accessToken: string, refreshToken: string): Promise<void> => {
  try {
    await timeout(
      Keychain.setGenericPassword("access_token", accessToken, { service: "access_token" }),
      5000
    );
    await timeout(
      Keychain.setGenericPassword("refresh_token", refreshToken, { service: "refresh_token" }),
      5000
    );
    //await Keychain.setGenericPassword('access_token', accessToken, { service: 'access_token' });
    //await Keychain.setGenericPassword('refresh_token', refreshToken, { service: 'refresh_token' });
    console.debug("Tokens stored securely");
  } catch (error) {
    console.debug("Error storing tokens", error);
    exceptionService.logError(error, "Auth.storeTokens");
    //throw error;
    return;
  }
};

// Retrieve tokens securely
export const getTokens = async (): Promise<Tokens | null> => {
  try {
    const accessToken = await safeGetAccessToken("access_token"); // Keychain.getGenericPassword({ service: 'access_token' });}catch{}
    const refreshToken = await safeGetAccessToken("refresh_token"); // await Keychain.getGenericPassword({ service: 'refresh_token' });}catch{}
    if (accessToken && refreshToken) {
      return {
        accessToken: accessToken.password,
        refreshToken: refreshToken.password,
      };
    }
    return null;
  } catch (error) {
    console.debug("Error retrieving tokens", error);
    exceptionService.logError(error, "Auth.getTokens");
    return null;
  }
};

async function safeGetAccessToken(tokenName: string, timeoutMs = 3000) {
  try {
    const result = await Promise.race([
      Keychain.getGenericPassword({ service: tokenName }),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
    ]);
    return result;
  } catch (error) {
    console.error("Error retrieving access or refresh token from Keychain:", error);
    exceptionService.logError(error, "Error retrieving access or refresh token from Keychain:");
    return null;
  }
}

// Refresh access token using refresh token
export const refreshAccessToken = async (refreshToken: string): Promise<string | null> => {
  try {
    const { data, error } = await authRequest<any>("/auth/refresh", { refreshToken });
    if (error) {
      console.debug("Error refreshing token", error);
      exceptionService.logError(error, "Auth.refreshAccessToken");
      return null;
    }
    return data.session.access_token;
  } catch (error) {
    console.debug("Error refreshing token", error);
    exceptionService.logError(error, "Auth.refreshAccessToken");
    return null;
  }
};

// // Decode JWT and check expiration
// export const decodeJWT = (token: string): DecodedJWT | null => {
//   try {
//     const decoded = jwtDecode<DecodedJWT>(token);
//     return decoded;
//   } catch (error) {
//     console.debug('Error decoding token', error);
//     exceptionService.logError(error, 'Auth.decodeJWT');
//     return null;
//   }
// };

// Check if the token is expired
// export const isTokenExpired = (token: string): boolean => {
//   try{
//     const decodedToken = decodeJWT(token);
//     if (decodedToken && decodedToken.exp) {
//       const currentTime = Date.now() / 1000;
//       return decodedToken.exp < currentTime;
//     }
//     return true;  // Assume expired if decoding fails
//   }catch(error){
//     exceptionService.logError(error, 'Auth.isTokenExpired');
//     return false;
//   }
// };

// Clear stored tokens
export const clearStoredTokens = async (): Promise<void> => {
  try {
    // Remove both access and refresh tokens from the secure storage
    await withTimeout(Keychain.resetGenericPassword({ service: "access_token" }));
    await withTimeout(Keychain.resetGenericPassword({ service: "refresh_token" }));
  } catch (error) {
    console.debug("Error clearing tokens", error);
    exceptionService.logError(error, "Auth.clearStoredTokens");
  }
};

async function withTimeout<T>(promise: Promise<T>, ms = 3000): Promise<T | null> {
  return Promise.race([
    promise,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
  ]);
}

export const signUp = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  phone: string,
  address: string,
  city: string,
  state: string,
  zip: string,
  profilePic: any,
  skipProfilePicUpload?: boolean
) => {
  try {
    // Create user in Supabase Auth
    const { error: authError, data } = await authRequest<any>("/auth/sign-up", {
      email,
      password,
      firstName,
      lastName,
      phone,
      zip,
      profilePic,
    });
    //if user was created, upload the profile picture
    if (data && data.user) {
      //update the profile table with address values
      //await userProfileService.updateProfileAddressAfterSignup(address, city, state, data.user.id);
      //await userProfileService.updateIsHostAccountAfterSignup(data.user.id, isHostAccount);
      //upload the profile picture
      // if(!skipProfilePicUpload)
      //   await userProfileService.updateProfilePicture(profilePic, data.user.id);
    }
    //log if auth error
    if (authError) {
      console.debug("Error signing up:", authError.message);
      exceptionService.logError(authError, "Auth.signUp");
    }
    let user = data?.user ?? null;
    return { user, authError };
  } catch (error) {
    exceptionService.logError(error, "Auth.signUp");
    throw error;
  }
};

export const signIn = async (email: string, password: string) => {
  try {
    const { data, error } = await authRequest<any>("/auth/sign-in", { email, password });
    return { data, error };
  } catch (error) {
    exceptionService.logError(error, "Auth.signIn");
    throw error;
  }
};

export const signOut = async () => {
  try {
    //await GoogleSignin.signOut();
    const accessToken = await getValidAccessToken();
    const { error } = accessToken
      ? await authRequest("/auth/sign-out", {}, accessToken)
      : { error: null };
    if (error) {
      console.debug(error);
      exceptionService.logError(error, "Auth.signOut");
    }
    return !error;
  } catch (error) {
    exceptionService.logError(error, "Auth.signOut");
    throw error;
  }
};

export const sendPasswordResetEmail = async (email: string) => {
  try {
    const { error } = await authRequest("/auth/password-reset-email", { email });
    if (error) {
      console.debug(error);
      exceptionService.logError(error, "Auth.sendPasswordResetEmail");
    }
    return !error;
  } catch (error) {
    exceptionService.logError(error, "Auth.sendPasswordResetEmail");
    throw error;
  }
};

export const updatePassword = async (newPassword: string) => {
  try {
    const accessToken = await getValidAccessToken();
    if (!accessToken) return false;
    const { error } = await authRequest(
      "/auth/update-password",
      { password: newPassword },
      accessToken
    );
    if (error) {
      console.debug("error on update", error);
      exceptionService.logError(error, "Auth.updatePassword");
    }
    return !error;
  } catch (error) {
    exceptionService.logError(error, "Auth.updatePassword");
    throw error;
  }
};

export const sendOTP = async (phoneNumber: string) => {
  try {
    const { error } = await authRequest("/auth/send-otp", { phone: stripPhoneNumber(phoneNumber) });
    if (!error) console.debug("OTP sent successfully!");
    else {
      console.debug(error);
      exceptionService.logError(error, "Auth.sendOTP");
    }
    return !error;
  } catch (error) {
    console.debug("Error sending OTP:", error);
    exceptionService.logError(error, "Auth.sendOTP");
    return false;
  }
};

export const verifyPhoneNumberOTP = async (phoneNumber: string, verificationCode: any) => {
  try {
    const { error } = await authRequest("/auth/verify-otp", {
      phone: stripPhoneNumber(phoneNumber),
      token: verificationCode,
    });
    if (!error) console.debug("Phone Number Verified Successfully");
    else {
      console.debug("Phone Number Not Verified");
      exceptionService.logError(error, "Auth.verifyPhoneNumberOTP");
    }
    return !error;
  } catch (error) {
    console.debug("Error sending OTP:", error);
    exceptionService.logError(error, "Auth.verifyPhoneNumberOTP");
    return false;
  }
};

// Check if the token is expired
export const isTokenExpired = (token: string): boolean => {
  try {
    const decodedToken = decodeJWT(token);
    if (decodedToken && decodedToken.exp) {
      const currentTime = Date.now() / 1000;
      return decodedToken.exp < currentTime;
    }
    return true; // Assume expired if decoding fails
  } catch (error) {
    exceptionService.logError(error, "Auth.isTokenExpired");
    return false;
  }
};
